@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.entrys

import org.example.project.manager.NodeScope

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardScreen
import org.example.project.dashboard.DashboardNode
import org.example.project.dashboard.ScreenManager
import org.example.project.dashboard.MobileDashboardScreen
import org.example.project.login.DesktopLoginScreen
import org.example.project.login.MobileLoginScreen
import org.example.project.enginepersonal.StempeluhrScreen
import org.example.project.manager.LumaticaApiSettings
import org.example.project.setup.CUSlumaticasetup
import org.example.project.setup.SetupApi
import org.example.project.enginemodule.ModulShopLayout
import org.example.project.service.GlobalManager
import org.example.project.service.LizenzService
import org.example.project.service.LoginService
import org.example.project.service.MobileAnsicht
import org.example.project.service.MobileAnsichtenApi
import org.example.project.service.RollenService
import org.example.project.service.ScreenConfigApi
import org.example.project.service.SystemPushService
import org.example.project.service.shouldForceLogoutAfterBuildChange
import org.w3c.dom.events.Event

private fun currentPath(): String =
    when (val path = window.location.pathname.trimEnd('/').lowercase().ifBlank { "/" }) {
        "/", "/login" -> "/"
        "/mobile", "/companion", "/setup", "/stempeluhr", "/store", "/store/mine" -> path
        else -> ""
    }

private fun navigateTo(path: String) {
    val target = if (path.startsWith("/")) path else "/$path"
    if (currentPath() == target) return
    window.history.pushState(null, "", target)
    window.dispatchEvent(Event("popstate"))
}

@Composable
fun CUSEntry() {
    LumaticaApiSettings.nodeIp = window.location.hostname
    LumaticaApiSettings.nodePort = window.location.port

    val companion = window.location.pathname.trimEnd('/').lowercase() == "/companion"
    var path by remember { mutableStateOf(currentPath()) }
    var setupLoaded by remember { mutableStateOf(false) }
    var sessionReady by remember { mutableStateOf(false) }
    var nodeEingerichtet by remember { mutableStateOf(false) }
    var desktopAuthRevision by remember { mutableStateOf(0) }
    var mobileAuthRevision by remember { mutableStateOf(0) }
    var mobileAnsichten by remember { mutableStateOf<List<MobileAnsicht>>(emptyList()) }
    val screenConfigVersion = ScreenConfigApi.version()
    val scope = rememberCoroutineScope()
    if (!LoginService.isLoggedIn()) {
        org.example.project.login.PublicWebsiteFooter(
            compact = path in setOf("/mobile", "/companion", "/driver", "/driver/register")
        )
    }

    LaunchedEffect(Unit) {
        if (window.location.pathname.trimEnd('/').lowercase() == "/login") {
            window.history.replaceState(null, "", "/")
        }
    }

    suspend fun initializeLoggedInSession(): Boolean {
        SystemPushService.ensureStarted()
        ScreenManager.applyScreenConfig(ScreenConfigApi.loadScreenConfig())
        LizenzService.loadCustomerScreenPrefs()
        LizenzService.syncKundenModuleState()
        RollenService.refreshCurrentUserRole(NodeScope.CUS)
        mobileAnsichten = MobileAnsichtenApi.available()
        return true
    }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { path = currentPath() }
        window.addEventListener("popstate", listener)
        onDispose {
            window.removeEventListener("popstate", listener)
        }
    }

    LaunchedEffect(Unit) {
        nodeEingerichtet = runCatching {
            SetupApi.loadCusNodeSettings()?.nodeEingerichtet
        }.getOrNull() ?: false
        setupLoaded = true

        LoginService.restoreSession()
        if (path == "/mobile") {
            if (LoginService.isLoggedIn()) LoginService.logout()
            sessionReady = true
            return@LaunchedEffect
        }
        if (!companion && shouldForceLogoutAfterBuildChange("CUS")) {
            LoginService.logout()
            sessionReady = true
            return@LaunchedEffect
        }

        if (!LoginService.isLoggedIn()) {
            sessionReady = true
            return@LaunchedEffect
        }
        val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
        if (!initialized) {
            LoginService.logout()
        }
        sessionReady = true
    }

    LaunchedEffect(
        sessionReady,
        GlobalManager.jsonDomainVersion("personal"),
        GlobalManager.jsonTopicVersion("mobile-ansichten.json")
    ) {
        if (sessionReady && LoginService.isLoggedIn()) {
            mobileAnsichten = MobileAnsichtenApi.available()
        }
    }

    LaunchedEffect(screenConfigVersion) {
        if (!LoginService.isLoggedIn()) return@LaunchedEffect
        runCatching { ScreenManager.applyScreenConfig(ScreenConfigApi.loadScreenConfig()) }
    }

    if (!setupLoaded) {
        org.example.project.utils.DashboardVerlaufHintergrund()
        return
    }
    if (!sessionReady) {
        org.example.project.utils.DashboardVerlaufHintergrund()
        return
    }
    if (!nodeEingerichtet && path != "/setup") {
        navigateTo("/setup")
        return
    }

    @Suppress("UNUSED_EXPRESSION")
    desktopAuthRevision

    when {
        path == "/setup" ->
            if (nodeEingerichtet) {
                navigateTo("/")
            } else {
                CUSlumaticasetup {
                    nodeEingerichtet = true
                    navigateTo("/")
                }
            }

        path == "/" ->
            if (LoginService.isLoggedIn() && LoginService.getUser()?.zugangDesktop == true) {
                DashboardScreen(node = DashboardNode.CUS) {
                    navigateTo("/")
                    desktopAuthRevision += 1
                }
            } else {
                if (LoginService.isLoggedIn()) LoginService.logout()
                DesktopLoginScreen(art = NodeScope.CUS) { _, _ ->
                    scope.launch {
                        val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
                        if (!initialized) {
                            LoginService.logout()
                            desktopAuthRevision += 1
                            return@launch
                        }
                        desktopAuthRevision += 1
                    }
                }
            }

        (path == "/mobile" || path == "/companion") -> {
            @Suppress("UNUSED_EXPRESSION")
            mobileAuthRevision
            if (LoginService.isLoggedIn() && (if (companion) LoginService.getUser()?.zugangCompanion == true else LoginService.getUser()?.zugangMobil == true)) {
                val sessionVersion = GlobalManager.sessionConfigVersion()
                val effectiveScreens = LizenzService.getEffectiveScreens()
                val allowed = ScreenManager.allowedMobileCusScreens(
                    effectiveScreens = effectiveScreens,
                    canRead = { RollenService.hasReadPermission(it) }
                ).toMutableSet().apply {
                    if (
                        "Schulungsverwaltung" in effectiveScreens &&
                        (if (companion) LoginService.getUser()?.zugangCompanion == true else LoginService.getUser()?.zugangMobil == true) &&
                        RollenService.hasReadPermission("MitarbeiterKalender")
                    ) {
                        add("MitarbeiterKalender")
                        add("AufgabenViewer")
                    }
                }
                val screens = ScreenManager.mobileDashboardScreens(DashboardNode.CUS_MOBILE)
                    .filter { it.key in allowed && MobileAnsichtenApi.active(mobileAnsichten, it.key, companion = companion) }
                @Suppress("UNUSED_EXPRESSION")
                sessionVersion
                MobileDashboardScreen(
                    art = NodeScope.CUS,
                    screens = screens,
                    onLogout = {
                    if (companion) scope.launch { org.example.project.service.CompanionBridgeService.disconnect() }
                    else mobileAuthRevision += 1
                }
                )
            } else {
                if (LoginService.isLoggedIn()) LoginService.logout()
                if (companion) {
                    LaunchedEffect(Unit) { org.example.project.service.CompanionBridgeService.disconnect() }
                    return
                }
                MobileLoginScreen(art = NodeScope.CUS) { _, _ ->
                    scope.launch {
                        val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
                        if (!initialized) {
                            LoginService.logout()
                            return@launch
                        }
                        mobileAuthRevision += 1
                    }
                }
            }
        }

        path == "/stempeluhr" ->
            StempeluhrScreen()

        path == "/store" || path == "/store/mine" -> {
            if (!LoginService.isLoggedIn() || LoginService.getUser()?.zugangDesktop != true) {
                if (LoginService.isLoggedIn()) LoginService.logout()
                DesktopLoginScreen(art = NodeScope.CUS) { _, _ ->
                    scope.launch {
                        val initialized = runCatching { initializeLoggedInSession() }.getOrElse { false }
                        if (!initialized) {
                            LoginService.logout()
                            desktopAuthRevision += 1
                            return@launch
                        }
                        desktopAuthRevision += 1
                        path = currentPath()
                    }
                }
            } else {
                ModulShopLayout()
            }
        }

        else -> org.example.project.utils.DashboardVerlaufHintergrund()
    }
}
